import type { FormDataOf } from '../../../../../types/templates';
import type { DynamicOptions } from '../../../../../types/client-handler';
import { findLeague, findTeam } from '../data/queries';
import type { TeamsSelection } from '../data/types';
import { matchdayPrefix } from '../data/folder-prefix';
import { disneyESPNLogoVsLogo } from '..';

type FormData = FormDataOf<typeof disneyESPNLogoVsLogo>;

const toColorOption = (hex: string) => ({ id: hex, value: hex, backgroundColor: hex });

const colorsForTeam = (league: string | undefined, teamId: string | undefined, slot: 'local' | 'visitor'): string[] =>
  findTeam(league, teamId)
    ?.variants.filter(v => (slot === 'visitor' ? !v.localOnly : !v.visitorOnly))
    .map(v => (slot === 'visitor' ? (v.visitorHex ?? v.hex) : v.hex)) ?? [];

// Pulls "33" out of "creativity/espn/logo-vs-logo/<league>/<prefix>-33".
const matchdayFromFolder = (folder: string, league: string, prefix: string): string | undefined => {
  const leaguePath = `creativity/espn/logo-vs-logo/${league}/`;
  if (!folder.startsWith(leaguePath)) return undefined;
  const last = folder.slice(leaguePath.length).split('/')[0];
  const m = new RegExp(`^${prefix}-(.+)$`).exec(last);
  return m?.[1];
};

const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

const matchdayOptions = (league: string, folders: string[] = []) => {
  const prefix = matchdayPrefix(league);
  const values = new Set<string>();
  for (const f of folders) {
    const md = matchdayFromFolder(f, league, prefix);
    if (md) values.add(md);
  }
  const sorted = Array.from(values).sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isInteger(na) && Number.isInteger(nb)) return na - nb;
    return a.localeCompare(b);
  });
  const labelPrefix = capitalize(prefix);
  return sorted.map(v => ({ value: v, label: `${labelPrefix} ${v}` }));
};

export const getDynamicOptions = (
  fieldKey: string,
  formData: FormData,
  baseOptions?: DynamicOptions,
  context?: { folders?: string[] }
): DynamicOptions | undefined => {
  const league = formData['league'] as string | undefined;
  const teams = formData['teams'] as TeamsSelection | undefined;

  if (fieldKey === 'teams') {
    return (findLeague(league)?.teams ?? []).map(t => ({
      id: t.value,
      value: t.value,
      label: t.label,
      imageUrl: t.variants[0]?.shieldUrl
    }));
  }

  if (fieldKey === 'local-color') {
    return colorsForTeam(league, teams?.slot1, 'local').map(toColorOption);
  }

  if (fieldKey === 'visitor-color') {
    return colorsForTeam(league, teams?.slot2, 'visitor').map(toColorOption);
  }

  if (fieldKey === 'matchday' && league) {
    return matchdayOptions(league, context?.folders);
  }

  return baseOptions;
};
